import { useLocation, useNavigate } from 'react-router-dom';
import { isNativeAndroid, isNativeIOS, sendToNative } from './NativeUtil';

function getSameOriginPath(url) {
  const value = typeof url === 'string' ? url.trim() : '';
  if (!value) {
    return null;
  }

  if (!value.startsWith('/') && !/^https?:\/\//.test(value)) {
    return null;
  }

  try {
    const parsedUrl = new URL(value, window.location.origin);
    if (parsedUrl.origin !== window.location.origin) {
      return null;
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (_error) {
    return null;
  }
}

function hasWebHistoryBackEntry() {
  const historyIndex = window.history.state?.idx;
  return typeof historyIndex === 'number' && historyIndex > 0;
}

function closeNativeWebView(returnUrl) {
  if (!isNativeIOS() && !isNativeAndroid()) {
    return false;
  }

  return sendToNative(JSON.stringify({ type: 'closeWebView', returnUrl }));
}

/**
 * HandleHistoryBack 훅
 * 버튼 클릭 시 호출할 수 있는 함수 반환
 */
export function useHandleHistoryBack(options = {}) {
  const {
    fallbackPath,
    fallbackState,
    nativeCloseFallbackDelayMs = null,
    preferNativeWebViewClose = false
  } = options;
  const navigate = useNavigate();
  const location = useLocation();

  return () => {
    const queryParams = new URLSearchParams(window.location.search);
    const locationState = location.state || {};
    const returnUrl = locationState.returnUrl || queryParams.get('returnUrl');
    const returnState = locationState.returnState;

    const handleNewTabClose = () => {
      if (queryParams.get('newTabOpen')) {
        window.close();
        return true;
      }
      return false;
    };

    const handlePushNotification = () => {
      if (queryParams.get('pushNotification')) {
        navigate('/home', { replace: true });
        return true;
      }
      return false;
    };

    const handleRedirectUrl = () => {
      const redirectUrl = queryParams.get('redirectUrl');
      if (redirectUrl) {
        navigate(redirectUrl, { replace: true });
        return true;
      }
      return false;
    };

    const handleRedirectFeedHome = () => {
      if (queryParams.get('redirectFeedHome')) {
        navigate('/feed', { replace: true });
        return true;
      }
      return false;
    };

    const navigateFallback = () => {
      if (!fallbackPath) {
        return false;
      }

      navigate(fallbackPath, { replace: true, state: fallbackState });
      return true;
    };

    const closeNativeWebViewWithFallback = (nativeReturnUrl) => {
      const didSendCloseMessage = closeNativeWebView(nativeReturnUrl);
      if (!didSendCloseMessage) {
        return false;
      }

      if (fallbackPath && typeof nativeCloseFallbackDelayMs === 'number') {
        window.setTimeout(navigateFallback, nativeCloseFallbackDelayMs);
      }

      return true;
    };

    const handleReturnUrl = () => {
      if (!returnUrl) {
        return false;
      }

      const sameOriginPath = getSameOriginPath(returnUrl);
      if (sameOriginPath) {
        navigate(sameOriginPath, { replace: true, state: returnState });
        return true;
      }

      return closeNativeWebViewWithFallback(returnUrl);
    };

    const handleNativeNavigation = () => {
      if (!locationState.fromNativeNavigation) {
        return false;
      }

      return closeNativeWebViewWithFallback(returnUrl);
    };

    if (handleNewTabClose()) return;
    if (handlePushNotification()) return;
    if (handleRedirectUrl()) return;
    if (handleRedirectFeedHome()) return;
    if (handleReturnUrl()) return;
    if (handleNativeNavigation()) return;
    if (preferNativeWebViewClose) {
      if (closeNativeWebViewWithFallback(returnUrl)) return;
      if (navigateFallback()) return;
    }

    if (!hasWebHistoryBackEntry()) {
      if (closeNativeWebViewWithFallback()) return;
      if (navigateFallback()) return;
    }

    // Default case: go back one step in history
    navigate(-1);
  };
}
